import { useAuth } from '../../contexts/auth/useAuth';
import styled from '@emotion/styled';
import VideoIcon from '@mui/icons-material/OndemandVideo';
import {
  Box,
  Button,
  CardActionArea,
  CardContent,
  Card,
  Grid,
  Typography,
} from '@mui/material';
import React from 'react';
import { PageTitle } from '../../components/PageTitle';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUsers } from '../../hooks/api/useUsers';
import { PageComponentType } from '../../types/pageComponent';
import PageComponent from './components/HomePageItems';

const AboutIcon = styled.span`
  display: flex;

  svg {
    width: 14px;
    height: 14px;
  }
`;
const Icon = styled.img`
  height: 50px;
`;

const StyledCard = styled(Card)`
  height: 200px;
  margin-bottom: 24px;
  padding: 0;
  overflow: visible;
  box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.08);
  background-color: ${import.meta.env.REACT_APP_BUILD === 'staging'
    ? 'var(--color-staging-navigation)'
    : 'var(--color-deep-ocean-blue)'};

  &:hover {
    background-color: var(--color-scifree-teal);
  }
`;

const StyledCardActionArea = styled(CardActionArea)`
  &.Mui-focusVisible,
  &:focus-visible {
    outline: 2px solid var(--color-scifree-teal);
    outline-offset: 4px;
    border-radius: 2px;
  }
` as typeof CardActionArea;

const StyledCardContent = styled(CardContent)`
  height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
`;

const Header = styled(Typography)`
  color: var(--color-white);
  overflow: hidden;
  text-overflow: ellipsis;
` as typeof Typography;

const Paragraph = styled(Typography)`
  color: var(--color-white);
  overflow: hidden;
`;

const Section = ({ title, icon, subtext, url }: PageComponentType) => {
  const { t } = useTranslation();
  const isExternal = url.includes('http');
  const cardContent = (
    <StyledCardContent>
      <Header variant="h3" gutterBottom>
        {t(title)}
      </Header>
      <Grid mb={2} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Paragraph variant="body1" sx={{ fontSize: '16px' }}>
          {t(subtext)}
        </Paragraph>
      </Grid>
      <Grid item sx={{ flexShrink: 0 }}>
        <AboutIcon>
          <Icon src={icon} alt="" aria-hidden />
        </AboutIcon>
      </Grid>
    </StyledCardContent>
  );
  return (
    <StyledCard>
      {isExternal ? (
        <StyledCardActionArea
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {cardContent}
        </StyledCardActionArea>
      ) : (
        <StyledCardActionArea component={Link} to={url}>
          {cardContent}
        </StyledCardActionArea>
      )}
    </StyledCard>
  );
};

function HomePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { accounts: accountList } = useUsers();

  const currentAccount =
    user?.[`https://api.jstscifree.com/app_metadata`]?.account;

  const university = Object.entries(accountList || {}).find(
    ([_key, value]) => value === currentAccount
  )?.[0];
  return (
    <React.Fragment>
      <PageTitle title="Home" />
      <Grid
        container
        sx={{
          background: '#F3F6F9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box padding={12}>
          <Typography variant="h1" sx={{ fontSize: 18, fontWeight: 500 }}>
            {t('home.welcomeBanner.header')}
          </Typography>
          <Typography variant="h2" sx={{ fontSize: 20 }}>
            {university}
          </Typography>
        </Box>
      </Grid>
      <Grid
        container
        spacing={30}
        justifyContent="space-between"
        padding={10}
        mt={10}
        sx={{ background: '#ffffff' }}
      >
        {PageComponent() &&
          PageComponent().map((page) => (
            <Grid item xs={12} sm={6} lg={4} xl={4} key={page.icon}>
              <Section
                title={page.title}
                icon={page.icon}
                subtext={page.subtext}
                url={page.url}
              />
            </Grid>
          ))}
      </Grid>
    </React.Fragment>
  );
}

export default HomePage;
